/**
 * `TaskRow` — one task in the Pendências list.
 *
 * Lines, in priority order:
 *   1. priority marker (urgent/high only) + title + status chip
 *   2. case badges (one per ref) + due date (overdue styled)
 *   3. latest-update note + relative time
 *
 * A caseRef that no longer resolves to a live case renders an "arquivado"
 * badge, NEVER an error — a task can outlive its case, and the handoff is
 * explicit that this must degrade gracefully. The case name is resolved
 * through the same registry index the Agents sidebar uses, so a folder-form
 * ref resolves too and an unknown one is labelled honestly rather than shown
 * as a raw id.
 */
import { useTranslation } from "react-i18next";
import { AlertCircle, Folder } from "lucide-react";

import { cn } from "@/lib/utils";
import { resolveCaseLabel } from "@/components/team-of-agents/case-label";
import { isOverdue } from "@lexiun/tasklist-core";
import type { TaskRow as TaskRecord } from "@/api/lx-tasklist";
import { PriorityBadge, PriorityDot, StatusChip } from "./task-visuals";

export interface TaskRowProps {
  task: TaskRecord;
  today: string;
  /** caseId → title, from the registry (see `buildCaseLabelIndex`). */
  caseNames: Map<string, string>;
  selected?: boolean;
  onOpen: (taskId: string) => void;
  /** Narrow host (the case context pane, ~300px): step the type back down.
   *  The full-page scale set in T1 crowds a sidebar column. */
  compact?: boolean;
}

export function TaskRow({
  task,
  today,
  caseNames,
  selected,
  onOpen,
  compact = false,
}: TaskRowProps) {
  const { t, i18n } = useTranslation();
  const overdue = isOverdue(task, today);

  return (
    <li
      data-testid={`task-row-${task.id}`}
      data-status={task.status}
      className={cn(
        "border-b border-cream-200/60 last:border-b-0 dark:border-coffee-800/60",
        selected && "bg-blue-50/60 dark:bg-blue-950/20",
      )}
    >
      <button
        type="button"
        onClick={() => onOpen(task.id)}
        className={cn(
          "flex w-full flex-col text-left hover:bg-cream-50 dark:hover:bg-coffee-900/50",
          compact ? "gap-1 px-3 py-2" : "gap-1.5 px-5 py-3.5",
        )}
      >
        <div className={cn("flex items-start", compact ? "gap-2" : "gap-2.5")}>
          {/* Urgency reads as a dot on the left; the WORD lives on the right. */}
          <PriorityDot priority={task.priority} />
          <span
            className={cn(
              "min-w-0 flex-1 font-medium leading-snug",
              compact ? "text-[12px]" : "text-[15px]",
              task.status === "done" || task.status === "dropped"
                ? "text-slate-400 line-through dark:text-slate-500"
                : "text-slate-800 dark:text-slate-100",
            )}
          >
            {task.title}
          </span>
          {/* All the labelling in one quiet right-hand column. */}
          <span className="flex shrink-0 items-center gap-1.5">
            <PriorityBadge priority={task.priority} />
            <StatusChip status={task.status} />
          </span>
        </div>

        <div className={cn(
            "flex flex-wrap items-center gap-y-1 text-slate-400 dark:text-slate-500",
            compact ? "gap-x-2 pl-0.5 text-[11px]" : "gap-x-3 pl-[18px] text-[12px]",
          )}>
          {task.caseRefs.map((ref) => {
            const label = resolveCaseLabel(ref, caseNames);
            return label ? (
              <span
                key={ref}
                data-testid={`task-case-${ref}`}
                className="inline-flex items-center gap-0.5 text-slate-500 dark:text-slate-400"
              >
                <Folder size={9} className="opacity-70" aria-hidden />
                {label}
              </span>
            ) : (
              // Case gone — the task survives it. Say "arquivado", not error.
              <span
                key={ref}
                data-testid={`task-case-archived-${ref}`}
                title={t("tasklist.archivedCaseTitle")}
                className="inline-flex items-center gap-0.5 rounded bg-cream-200/60 px-1 py-0.5 text-slate-500 dark:bg-coffee-700/40 dark:text-slate-400"
              >
                <AlertCircle size={9} className="opacity-70" aria-hidden />
                {t("tasklist.archivedCase")}
              </span>
            );
          })}

          {task.dueDate && (
            <span
              data-testid={`task-due-${task.id}`}
              className={cn(
                "tabular-nums",
                overdue
                  ? "font-medium text-red-600 dark:text-red-400"
                  : "text-slate-400 dark:text-slate-500",
              )}
            >
              {overdue ? t("tasklist.overduePrefix") : t("tasklist.duePrefix")}{" "}
              {new Date(`${task.dueDate}T00:00:00`).toLocaleDateString(i18n.language, {
                day: "2-digit",
                month: "short",
              })}
            </span>
          )}
        </div>

        {task.latestUpdate?.note && (
          <div className={cn(
            "truncate font-light text-slate-400 dark:text-slate-500",
            compact ? "pl-0.5 text-[11px]" : "pl-[18px] text-[12.5px]",
          )}>
            {task.latestUpdate.note}
          </div>
        )}
      </button>
    </li>
  );
}
